// Refactored by j-funk into functional component 2023/09
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';

import { Button } from 'react-bootstrap';
import { Table, Column, Cell } from 'fixed-data-table-2';
// eslint-disable-next-line css-modules/no-unused-class
import s from './GenePredictions.css';

import PredictionCell from '../PredictionCell';
import Tooltip from '../Tooltip';
import Filter from '../Filter';

const NameCell = ({ rowIndex, data, col, ...props }) => {
  return <Cell {...props}>{data[rowIndex][col]}</Cell>;
};

// eslint-disable-next-line react/prop-types
const HighlightCell = ({ rowIndex, data, highlightGenes, col, ...props }) => {
  return (
    <Cell {...props}>
      {highlightGenes[data[rowIndex][col]] && (
        <Tooltip text="Gene is shown in the network">
          <i className="fa fa-circle" />
        </Tooltip>
      )}
    </Cell>
  );
};

// eslint-disable-next-line react/prop-types
const AddCell = ({ rowIndex, data, onGeneAdd, onGeneRemove, ...props }) => {
  return (
    <Cell {...props}>
      {/* eslint-disable-next-line react/prop-types */}
      {data[rowIndex].query
        ? onGeneRemove && (
            <Tooltip text="Remove gene from the query (will re-run network inference)">
              <Button
                bsSize="xsmall"
                bsStyle="danger"
                onClick={() => onGeneRemove(data[rowIndex])}
              >
                <i className="fa fa-minus-circle" />
              </Button>
            </Tooltip>
          )
        : onGeneAdd && (
            <Tooltip text="Add gene to the query (will re-run network inference)">
              <Button bsSize="xsmall" onClick={() => onGeneAdd(data[rowIndex])}>
                <i className="fa fa-plus-circle" />
              </Button>
            </Tooltip>
          )}
    </Cell>
  );
};

const GenePredictionTable = ({
  predictions,
  onRowClick,
  onGeneAdd,
  onGeneRemove,
  highlightGenes,
  width,
}) => (
  <Table
    rowHeight={40}
    rowsCount={predictions.length}
    width={width}
    height={15 * 40 + 30 + 2}
    headerHeight={30}
    rowClassNameGetter={() => s.row}
    onRowClick={onRowClick && ((d, i) => onRowClick(predictions[i]))}
  >
    {highlightGenes && (
      <Column
        width={20}
        cell={
          <HighlightCell
            data={predictions}
            highlightGenes={highlightGenes}
            col="standard_name"
          />
        }
      />
    )}
    <Column
      header={<Cell>SYMBOL</Cell>}
      width={80}
      cell={<NameCell data={predictions} col="standard_name" />}
    />
    <Column
      header={<Cell>NAME</Cell>}
      width={80}
      cell={<NameCell data={predictions} col="description" />}
      flexGrow={1}
    />
    <Column
      header={
        <Cell>
          SCORE
          <Tooltip text="Average weight of connections to the query genes" />
        </Cell>
      }
      width={110}
      cell={<PredictionCell data={predictions} col="score" />}
    />
    {(onGeneAdd || onGeneRemove) && (
      <Column
        width={50}
        cell={
          <AddCell
            onGeneAdd={onGeneAdd}
            onGeneRemove={onGeneRemove}
            data={predictions}
          />
        }
      />
    )}
  </Table>
);

const FilteredGenePredictionTable = Filter({
  dataField: 'predictions',
  fields: ['standard_name', 'description'],
})(GenePredictionTable);

/*
 * Component to display predicted genes (e.g. in a network or predicted
 * to a term)
 */
const GenePredictions = ({
  title,
  predictions,
  highlightGenes = [],
  containerWidth,
  onGeneSelect,
  onGeneAdd,
  onGeneRemove,
}) => {
  const [_highlightGenes, setHighlightGenes] = useState({});

  useEffect(() => {
    const newHighlightGenes = highlightGenes.reduce((o, v) => {
      return { ...o, [v.standard_name]: true };
    }, {});
    setHighlightGenes(newHighlightGenes);
  }, [highlightGenes]);

  return (
    <div>
      {title}
      <FilteredGenePredictionTable
        predictions={predictions}
        onRowClick={onGeneSelect}
        width={containerWidth}
        highlightGenes={_highlightGenes}
        onGeneAdd={onGeneAdd}
        onGeneRemove={onGeneRemove}
        saveFields={['standard_name', 'description', 'entrez', 'score']}
        saveHeaders={['SYMBOL', 'NAME', 'ENTREZ_ID', 'SCORE']}
        saveFile="network_predictions"
      />
    </div>
  );
};

export default withStyles(s)(GenePredictions);
